import React, { useState } from 'react';
import { queryCompliance } from './api/api';
import { Jurisdiction, QueryResponse, Outcome } from './types';
import './App.css';

const App: React.FC = () => {
    const [question, setQuestion] = useState('');
    const [jurisdiction, setJurisdiction] = useState<Jurisdiction>(Jurisdiction.DPDP);
    const [response, setResponse] = useState<QueryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const res = await queryCompliance({ question, jurisdiction });
            setResponse(res);
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-container">
            <header className="header">
                <h1>GST Compliance RAG</h1>
                <p className="subtitle">Advanced RAG System for Indian GST Queries</p>
            </header>

            <main className="main-content">
                <form onSubmit={handleSubmit} className="query-form glass">
                    <div className="form-group">
                        <label htmlFor="jurisdiction">Jurisdiction</label>
                        <select
                            id="jurisdiction"
                            value={jurisdiction}
                            onChange={(e) => setJurisdiction(e.target.value as Jurisdiction)}
                        >
                            <option value={Jurisdiction.DPDP}>DPDP (India)</option>
                            <option value={Jurisdiction.GDPR}>GDPR (EU)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="question">GST Compliance Question</label>
                        <textarea
                            id="question"
                            placeholder="e.g., What are the penalties for late GST filing?"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            required
                        />
                    </div>

                    <button type="submit" disabled={loading || !question}>
                        {loading ? 'Analyzing...' : 'Query System'}
                    </button>
                </form>

                {error && (
                    <div className="error-view glass animate-in">
                        <h3>System Error</h3>
                        <p>{error}</p>
                    </div>
                )}

                {response && response.outcome === Outcome.ABSTAINED && (
                    <div className="abstain-view glass animate-in">
                        <div className="status-badge low">ABSTAINED</div>
                        <h3>System Abstention</h3>
                        <p className="reason">{response.reason}</p>
                        <div className="metrics">
                            <span>Confidence: <strong>{response.confidence}</strong></span>
                        </div>
                    </div>
                )}

                {response && response.outcome === Outcome.ANSWERED && (
                    <div className="answer-view glass animate-in">
                        <div className={`status-badge ${response.confidence.toLowerCase()}`}>
                            {response.confidence} CONFIDENCE
                        </div>
                        <h3>Validated Answer</h3>
                        <p className="answer-text">{response.answer}</p>

                        <div className="meta-footer">
                            <div className="metric">
                                Faithfulness Score: <strong>{(response.faithfulness_score * 100).toFixed(0)}%</strong>
                            </div>
                            <div className="sources">
                                <h4>Source Clauses</h4>
                                <ul>
                                    {response.grounding_nodes.map(node => (
                                        <li key={node.id}>
                                            <span className="clause-id">ID: {node.metadata.clause_id}</span>
                                            <p className="clause-preview">{node.text.substring(0, 150)}...</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default App;
